import {
  Box,
  Button,
  Menu,
  MenuItem,
  SvgIcon,
  SvgIconProps,
} from "@mui/material"
import { GridApiPro } from "@mui/x-data-grid-pro"
import { MutableRefObject, useState } from "react"
import appTheme from "../../../../theme/appTheme"
import appIcons from "@utils/appIcons"
import { IconType } from "@utils/iconType"

// One icon family on the whole grid: DataGridPro ships Material icons for its
// own controls (sorting, column menu, detail toggles, panels), which sat
// beside the Untitled UI set on the same row. These slot overrides put every
// built-in glyph on the house set, 16px as everywhere else inside controls.
function houseGridIcon(icon: IconType, rotateDeg?: number) {
  const HouseGridIcon = (props: SvgIconProps) => (
    <SvgIcon
      inheritViewBox
      {...props}
      component={icon}
      sx={{
        width: "16px",
        height: "16px",
        fill: "none",
        stroke: "currentColor",
        transform: rotateDeg ? `rotate(${rotateDeg}deg)` : undefined,
      }}
    />
  )
  return HouseGridIcon
}

export const studentsGridIconSlots = {
  columnSelectorIcon: houseGridIcon(appIcons.grid01),
  openFilterButtonIcon: houseGridIcon(appIcons.funnel),
  columnFilteredIcon: houseGridIcon(appIcons.funnel),
  densityCompactIcon: houseGridIcon(appIcons.list),
  densityStandardIcon: houseGridIcon(appIcons.list),
  densityComfortableIcon: houseGridIcon(appIcons.list),
  exportIcon: houseGridIcon(appIcons.download02),
  columnMenuIcon: houseGridIcon(appIcons.dotsHorizontal),
  moreActionsIcon: houseGridIcon(appIcons.dotsHorizontal),
  columnSortedAscendingIcon: houseGridIcon(appIcons.chevronDown, 180),
  columnSortedDescendingIcon: houseGridIcon(appIcons.chevronDown),
  detailPanelExpandIcon: houseGridIcon(appIcons.chevronRight),
  detailPanelCollapseIcon: houseGridIcon(appIcons.chevronDown),
  filterPanelDeleteIcon: houseGridIcon(appIcons.xClose),
}

const ExportIcon = houseGridIcon(appIcons.download02)

// Quiet, text-forward controls: neutral label colour, subtle 16px icon, house
// focus ring. Not primary-blue buttons — the views row is the loud control;
// these read calm on the band directly above the grid. Shared with the
// [+ Filter] picker and the drift actions that sit on the same band.
export const controlButtonSx = {
  color: appTheme.palette.text.secondary,
  whiteSpace: "nowrap",
  flexShrink: 0,
  // Pill-shaped like every other control on its band — a square button in a
  // row of rounded chips read as a foreign object (Mayer).
  borderRadius: "16px",
  px: "10px",
  "& .MuiButton-startIcon": { marginRight: "6px" },
  "&:focus-visible": {
    outline: `2px solid ${appTheme.palette.primary.main}`,
    outlineOffset: "2px",
  },
} as const

// Download the current rows (CSV / print) — filters and columns apply. The
// right-aligned action on the band; it sits apart from the filter controls so
// the row reads "conditions on the left, export on the right".
export const ExportControl = (props: {
  gridApiRef: MutableRefObject<GridApiPro>
}) => {
  const { gridApiRef } = props
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null)

  return (
    <Box sx={{ flexShrink: 0, display: "inline-flex" }}>
      <Button
        size="small"
        startIcon={<ExportIcon />}
        onClick={(event) => setExportAnchor(event.currentTarget)}
        sx={controlButtonSx}
      >
        Export
      </Button>
      <Menu
        anchorEl={exportAnchor}
        open={exportAnchor !== null}
        onClose={() => setExportAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            const api = gridApiRef.current
            if (typeof api?.exportDataAsCsv === "function") {
              api.exportDataAsCsv()
            }
            setExportAnchor(null)
          }}
        >
          Download as CSV
        </MenuItem>
        <MenuItem
          onClick={() => {
            const api = gridApiRef.current
            if (typeof api?.exportDataAsPrint === "function") {
              api.exportDataAsPrint()
            }
            setExportAnchor(null)
          }}
        >
          Print
        </MenuItem>
      </Menu>
    </Box>
  )
}
